import { NextPageContext } from "next";

import { getDeals } from "api/requests/getDeals";
import { getPromotions } from "api/requests/getPromotions";
import { Coordinates } from "custom-types/Coordinates";
import { Deal } from "custom-types/Deals";
import { Promotion } from "custom-types/Promotions";
import {
  getBogosData,
  getDealsData,
  getPromotionsData,
} from "redux/selectors/deals";
import { AppStore } from "redux/store";

export type DealsState = {
  bogos: Deal[];
  dealCategories: string[];
  deals: Deal[];
  moreDealsAvailable: boolean;
  morePromosAvailable: boolean;
  promotions: Promotion[];
};

export const initialState: DealsState = {
  bogos: [],
  dealCategories: [],
  deals: [],
  moreDealsAvailable: true,
  morePromosAvailable: true,
  promotions: [],
};

export const DEALS_LOADED = "DEALS_LOADED";
export const APPEND_DEALS = "APPEND_DEALS";
export const PROMOTIONS_LOADED = "PROMOTIONS_LOADED";
export const APPEND_PROMOTIONS = "APPEND_PROMOTIONS";
export const BOGOS_LOADED = "BOGOS_LOADED";

export type DealsLoadedAction = {
  type: typeof DEALS_LOADED;
  deals: Deal[];
  dealCategories: string[];
  take: number;
};

export type AppendDealsAction = {
  type: typeof APPEND_DEALS;
  newDeals: Deal[];
  take: number;
};

export type PromotionsLoadedAction = {
  type: typeof PROMOTIONS_LOADED;
  promotions: Promotion[];
};

export type AppendPromotionsAction = {
  type: typeof APPEND_PROMOTIONS;
  newPromotions: Promotion[];
  take: number;
};

export type BogosLoadedAction = {
  type: typeof BOGOS_LOADED;
  bogos: Deal[];
};

export type DealsActions =
  | DealsLoadedAction
  | AppendDealsAction
  | BogosLoadedAction
  | PromotionsLoadedAction
  | AppendPromotionsAction;

export const getDealsState = async (
  ctx: NextPageContext<AppStore>,
  coordinates: Coordinates,
  countryCode: string
) => {
  const { dispatch, getState } = ctx.store;
  const currentState = getState();
  const currentDeals = getDealsData(currentState);

  if (!currentDeals.length) {
    const { deals, dealCategories } = await getDeals(
      coordinates,
      countryCode,
      0,
      8
    );

    dispatch<DealsLoadedAction>({
      dealCategories,
      deals,
      take: 8,
      type: DEALS_LOADED,
    });
  }
};

export const getPromotionsState = async (
  ctx: NextPageContext<AppStore>,
  coordinates: Coordinates,
  countryCode: string
) => {
  const { dispatch, getState } = ctx.store;
  const currentState = getState();
  const currentPromotions = getPromotionsData(currentState);

  if (!currentPromotions.length) {
    const { promotions } = await getPromotions(coordinates, countryCode, 0, 6);

    dispatch<PromotionsLoadedAction>({
      promotions,
      type: PROMOTIONS_LOADED,
    });
  }
};

export const getBogosState = async (
  ctx: NextPageContext<AppStore>,
  coordinates: Coordinates,
  countryCode: string
) => {
  const { dispatch, getState } = ctx.store;
  const currentState = getState();
  const currentBogos = getBogosData(currentState);

  if (!currentBogos.length) {
    const { deals: bogos } = await getDeals(coordinates, countryCode, 0, 16, {
      "filter[kind]": "bogo",
    });

    dispatch<BogosLoadedAction>({
      bogos,
      type: BOGOS_LOADED,
    });
  }
};

export const dealsReducer = (
  state = initialState,
  action: DealsActions
): DealsState => {
  switch (action.type) {
    case DEALS_LOADED:
      return {
        ...state,
        dealCategories: action.dealCategories,
        deals: action.deals,
        moreDealsAvailable: (action.deals || []).length >= action.take,
      };
    case APPEND_DEALS:
      return {
        ...state,
        deals: [...state.deals, ...action.newDeals],
        moreDealsAvailable: (action.newDeals || []).length >= action.take,
      };
    case PROMOTIONS_LOADED:
      return {
        ...state,
        promotions: action.promotions,
      };
    case APPEND_PROMOTIONS:
      return {
        ...state,
        morePromosAvailable: (action.newPromotions || []).length >= action.take,
        promotions: [...state.promotions, ...action.newPromotions],
      };
    case BOGOS_LOADED:
      return {
        ...state,
        bogos: action.bogos,
      };
    default:
      return state;
  }
};

export default dealsReducer;
